import React, { useState, useEffect, useMemo } from 'react'

const IMMEDIATE_RELATIONSHIPS = new Set([
  'Spouse', 'Wife', 'Husband', 'Son', 'Daughter', 'Child',
  'Father', 'Mother', 'Parent', 'Brother', 'Sister', 'Sibling'
])

function FilterDrawer({ isOpen, onClose, people = [], selectedPeopleIds, onApply }) {
  const [tempSelected, setTempSelected] = useState(new Set())
  const [searchQuery, setSearchQuery] = useState('')

  // Reset temp selection when drawer opens
  useEffect(() => {
    if (isOpen) {
      setTempSelected(new Set(selectedPeopleIds))
      setSearchQuery('')
    }
  }, [isOpen, selectedPeopleIds])

  const togglePerson = (personId) => {
    setTempSelected(prev => {
      const next = new Set(prev)
      if (next.has(personId)) {
        next.delete(personId)
      } else {
        next.add(personId)
      }
      return next
    })
  }

  const handleApply = () => {
    onApply(tempSelected)
    onClose()
  }

  const handleClear = () => {
    setTempSelected(new Set())
  }

  // Filter and group people
  const { immediate, extended } = useMemo(() => {
    const query = searchQuery.toLowerCase()
    const filtered = people.filter(p => {
      if (!query) return true
      const fullName = `${p.firstName || ''} ${p.lastName || ''}`.toLowerCase()
      return fullName.includes(query)
    })

    const imm = []
    const ext = []

    filtered.forEach(p => {
      if (p.isCurrentUser || IMMEDIATE_RELATIONSHIPS.has(p.relationship)) {
        imm.push(p)
      } else {
        ext.push(p)
      }
    })

    // Sort: current user first in immediate
    imm.sort((a, b) => {
      if (a.isCurrentUser) return -1
      if (b.isCurrentUser) return 1
      return (a.firstName || '').localeCompare(b.firstName || '')
    })

    ext.sort((a, b) => (a.firstName || '').localeCompare(b.firstName || ''))

    return { immediate: imm, extended: ext }
  }, [people, searchQuery])

  const highlightMatch = (text) => {
    if (!searchQuery) return text
    const idx = text.toLowerCase().indexOf(searchQuery.toLowerCase())
    if (idx === -1) return text
    return (
      <>
        {text.slice(0, idx)}
        <span className="bg-yellow-200 font-bold">{text.slice(idx, idx + searchQuery.length)}</span>
        {text.slice(idx + searchQuery.length)}
      </>
    )
  }

  const renderPersonRow = (person) => {
    const isSelected = tempSelected.has(person.id)
    const fullName = `${person.firstName || ''}${person.lastName ? ` ${person.lastName}` : ''}`

    return (
      <button
        key={person.id}
        onClick={() => togglePerson(person.id)}
        className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left ${
          isSelected ? 'bg-fb-olive/5' : ''
        }`}
      >
        {/* Checkbox */}
        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
          isSelected ? 'bg-fb-olive border-fb-olive' : 'border-gray-300'
        }`}>
          {isSelected && (
            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>

        {/* Avatar */}
        <div
          className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
          style={{ backgroundColor: person.avatarColor || '#6B7F4A' }}
        >
          {person.firstName?.[0]?.toUpperCase() || '?'}
        </div>

        {/* Name + relationship */}
        <div className="flex-1 min-w-0">
          <p className={`font-serif text-base truncate ${isSelected ? 'text-fb-olive font-bold' : 'text-gray-800'}`}>
            {highlightMatch(fullName)}
          </p>
          {person.relationship && (
            <p className="font-serif text-xs text-gray-500">{person.relationship}</p>
          )}
        </div>

        {/* Story count badge */}
        {(person.storyCount > 0) && (
          <span className="font-serif text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full whitespace-nowrap">
            {person.storyCount} {person.storyCount === 1 ? 'story' : 'stories'}
          </span>
        )}
      </button>
    )
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Drawer */}
      <div className="relative w-full max-w-[520px] bg-white shadow-xl flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="font-serif font-bold text-xl text-gray-900">Filter by Family Member</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search */}
        <div className="px-6 py-3 border-b border-gray-100">
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search family members..."
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 font-serif text-base focus:outline-none focus:ring-2 focus:ring-fb-olive focus:border-transparent"
            />
          </div>
        </div>

        {/* People list */}
        <div className="flex-1 overflow-y-auto">
          {immediate.length > 0 && (
            <div>
              <div className="px-6 py-2 bg-gray-50 border-b border-gray-100">
                <span className="font-serif text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Immediate Family
                </span>
              </div>
              {immediate.map(renderPersonRow)}
            </div>
          )}

          {extended.length > 0 && (
            <div>
              <div className="px-6 py-2 bg-gray-50 border-b border-gray-100">
                <span className="font-serif text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Extended Family
                </span>
              </div>
              {extended.map(renderPersonRow)}
            </div>
          )}

          {immediate.length === 0 && extended.length === 0 && (
            <div className="px-6 py-12 text-center">
              <p className="font-serif text-gray-500">No family members found</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between">
          <span className="font-serif text-sm text-gray-600">
            {tempSelected.size} {tempSelected.size === 1 ? 'person' : 'people'} selected
          </span>
          <div className="flex items-center gap-3">
            <button
              onClick={handleClear}
              className="font-serif text-sm text-gray-600 hover:text-gray-800 px-3 py-2"
            >
              Clear
            </button>
            <button
              onClick={handleApply}
              className="bg-fb-olive hover:bg-fb-olive/90 text-white font-serif font-semibold text-sm px-5 py-2.5 rounded-lg transition-colors"
            >
              Show Their Stories
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FilterDrawer
